/**
 * StoreCatalogueService
 * Store admin read surface for the merch catalogue.
 * Route: api/store-catalogue
 * Controller: StoreCatalogueController.cs
 *
 * Separate from ProductService because these are admin-only, ShopAdmin-gated
 * queries that paginate and aggregate server-side. `ProductService.getAll()`
 * returns the entire table — prefer `listView` here for any grid.
 */
import { BaseService } from '../base/BaseService';
const EMPTY_PRODUCT_PAGE = {
    items: [],
    totalCount: 0,
    skip: 0,
    take: 0,
    hasMore: false,
};
const EMPTY_STOCK_PAGE = {
    items: [],
    totalCount: 0,
    skip: 0,
    take: 0,
    hasMore: false,
    totalOnHand: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
};
export class StoreCatalogueService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? StoreCatalogueService.DEFAULT_SERVICE_PATH,
            basePath: StoreCatalogueService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== PRODUCTS ====================
    /**
     * Filtered, sorted, paged products with a total count.
     * POST /api/store-catalogue/products/list-view
     */
    async listProducts(filter) {
        const response = await this.listProductsRaw(filter);
        return response.data ?? EMPTY_PRODUCT_PAGE;
    }
    listProductsRaw(filter) {
        return this.http.post(this.buildUrl('products/list-view'), filter ?? {});
    }
    /**
     * Headline catalogue and stock figures.
     * GET /api/store-catalogue/products/stats/overview
     */
    async productStats() {
        const response = await this.productStatsRaw();
        return response.data ?? null;
    }
    productStatsRaw() {
        return this.http.get(this.buildUrl('products/stats/overview'));
    }
    /**
     * Product counts, average price and stock value grouped by type.
     * GET /api/store-catalogue/products/stats/by-type
     */
    async productStatsByType() {
        const response = await this.productStatsByTypeRaw();
        return response.data ?? [];
    }
    productStatsByTypeRaw() {
        return this.http.get(this.buildUrl('products/stats/by-type'));
    }
    // ==================== STOCK ====================
    /**
     * Enriched stock rows — product, colour, size and location names resolved
     * server-side — plus on-hand and value totals across the whole match set.
     *
     * Use this rather than `ProductStockService.getAll()`, which returns four bare ints
     * per row and leaves the joins to the caller.
     * POST /api/store-catalogue/stock/list-view
     */
    async listStock(filter) {
        const response = await this.listStockRaw(filter);
        return response.data ?? EMPTY_STOCK_PAGE;
    }
    listStockRaw(filter) {
        return this.http.post(this.buildUrl('stock/list-view'), filter ?? {});
    }
    /**
     * Stock at or below its minimum level.
     * POST /api/store-catalogue/stock/low-stock/list-view
     */
    async listLowStock(filter) {
        const response = await this.listLowStockRaw(filter);
        return response.data ?? EMPTY_STOCK_PAGE;
    }
    listLowStockRaw(filter) {
        return this.http.post(this.buildUrl('stock/low-stock/list-view'), filter ?? {});
    }
}
StoreCatalogueService.DEFAULT_SERVICE_PATH = 'products';
StoreCatalogueService.BASE_PATH = 'api/store-catalogue';
//# sourceMappingURL=StoreCatalogueService.js.map